import React from 'react';
import { Award, Clock, Users, Zap, Shield, Headphones, TrendingUp, Globe } from 'lucide-react';

export default function WhyChooseUs() {
  const reasons = [
    {
      icon: Award,
      title: "Industry Expertise",
      description: "Over a decade of specialized experience in coding, marking, and retail technology solutions",
      color: "from-amber-500 to-orange-600",
      delay: "0"
    },
    {
      icon: Zap,
      title: "Cutting-Edge Technology",
      description: "Latest innovations in printing, scanning, and digital retail systems for optimal performance",
      color: "from-blue-500 to-cyan-600",
      delay: "100"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "Expert technical team providing 24/7 customer support and comprehensive training programs",
      color: "from-purple-500 to-pink-600",
      delay: "200"
    },
    {
      icon: Shield,
      title: "Reliable Solutions",
      description: "Proven track record of delivering robust, durable systems with minimal downtime",
      color: "from-emerald-500 to-teal-600",
      delay: "300"
    },
    {
      icon: Clock,
      title: "Fast Implementation",
      description: "Quick deployment and seamless integration with your existing business operations",
      color: "from-rose-500 to-red-600",
      delay: "400"
    },
    {
      icon: TrendingUp,
      title: "Cost-Effective",
      description: "Competitive pricing with exceptional ROI and long-term value for your investment",
      color: "from-indigo-500 to-purple-600",
      delay: "500"
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Worldwide service network with local support and international quality standards",
      color: "from-green-500 to-emerald-600",
      delay: "600"
    },
    {
      icon: Headphones,
      title: "Custom Solutions",
      description: "Tailored systems designed specifically for your unique business requirements",
      color: "from-violet-500 to-purple-600",
      delay: "700"
    }
  ];

  const stats = [
    { number: "10+", label: "Years Experience", color: "text-blue-600" },
    { number: "500+", label: "Happy Clients", color: "text-emerald-600" },
    { number: "24/7", label: "Support Available", color: "text-purple-600" },
    { number: "99%", label: "Customer Satisfaction", color: "text-amber-600" }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-700 via-black-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      {/* <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-3/4 left-1/2 w-64 h-64 bg-emerald-500 rounded-full mix-blend-screen filter blur-3xl opacity-10 animate-pulse delay-2000"></div>
      </div> */}

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-5">
        <div className="h-full w-full bg-grid-pattern"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent mb-6">
            Why Choose Us?
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed font-poppins">
            We deliver exceptional technology solutions that drive your business forward with 
            innovation, reliability, and unmatched customer service.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center group"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 hover:bg-opacity-20 transition-all duration-300 transform hover:scale-105">
                <div className={`text-3xl md:text-4xl font-bold ${stat.color} mb-2`}>
                  {stat.number}
                </div>
                <div className="text-blue-100 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Reasons grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="group relative"
              style={{ 
                animation: `fadeInUp 0.6s ease-out ${parseInt(reason.delay) + 800}ms both`
              }}
            >
              {/* Card */}
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-6 h-full border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-500 transform hover:-translate-y-2 hover:scale-105">
                {/* Icon container */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${reason.color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <reason.icon className="w-8 h-8 text-white" />
                  </div>
                  {/* Glow effect */}
                  <div className={`absolute inset-0 w-16 h-16 rounded-2xl bg-gradient-to-r ${reason.color} opacity-0 group-hover:opacity-30 filter blur-xl transition-opacity duration-300`}></div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-100 transition-colors duration-300">
                  {reason.title}
                </h3>
                <p className="text-blue-100 leading-relaxed text-sm font-poppins">
                  {reason.description}
                </p>

                {/* Hover border effect */}
                <div className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:${reason.color} opacity-0 group-hover:opacity-50 transition-all duration-500`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        {/* <div className="text-center mt-16">
          <div className="inline-flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 bg-white bg-opacity-10 backdrop-blur-lg rounded-full px-8 py-6 border border-white border-opacity-20">
            <span className="text-lg font-medium text-white">
              Ready to experience the difference?
            </span>
            <button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
              Contact Us Today
            </button>
          </div>
        </div> */}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </section>
  );
}